import { states } from 'config';
import { Storage } from 'store/api/storage';
import { Product } from 'store/api/product/types';
import { PaymentMethod } from 'store/api/payment-methods';
import { FormValues } from 'pages/products/check-out/types';
import {
  User,
  Company,
  Timezone,
  IdObject,
  Nullable,
  EventType,
  BaseEntity,
  PayoutType,
  UploadFile,
  Commission,
  BaseResponse,
  CheckoutType,
  CloverDevice,
  SearchRequest,
  CloverPayment,
  PaginationData,
  EventLocationType,
  PaginationRequest,
} from 'types';

import { Refund } from '../refund';
import { Payout } from '../payout';
import { Customer } from '../customers';
import { Employee } from '../employees';
import { CartItem } from '../cart/types';
import { Inventory } from '../inventory';
import { Chargeback } from '../accounting';
import { StockOrder } from '../stock-orders';
import { EventOrder } from '../event-orders';
import { StockTransfer } from '../stock-transfers';

export interface WeeklySchedule {
  to?: number;
  day: string;
  from?: number;
}

export interface Event extends IdObject {
  name: string;
  tax?: number;
  zip?: number;
  city?: string;
  link?: string;
  phone?: string;
  email?: string;
  notes?: string;
  rating?: number;
  address?: string;
  isClose: boolean;
  endDate?: string;
  vendors?: string;
  storage: Storage;
  photoUrl?: string;
  storageId: number;
  company?: Company;
  leaderId?: number;
  product?: Product;
  lastEditor?: User;
  timezoneId: number;
  timezone: Timezone;
  theme?: BaseEntity;
  startDate?: string;
  updatedAt?: string;
  attendance?: string;
  sellingDays?: number;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  ratingMessage?: string;
  leader?: EventTeamMember;
  payments: EventPayment[];
  finalPrice: string | null;
  companyId?: Company['id'];
  locations: EventLocation[];
  status: EventStatus | null;
  state?: keyof typeof states;
  floorPlanLink: string | null;
  dayResults?: EventDayResult[];
  notesForClient: string | null;
  cloverDevices?: CloverDevice[];
  teamMembers: EventTeamMember[];
  floorPlanFileUrl: string | null;
  bookingTeamNotes: string | null;
  weeklySchedule?: WeeklySchedule[];
  questionnaires: EventQuestionnaire[];
  isManualCreditPaymentEnabled: boolean;
  selectedLocations: EventLocation['id'][];
  schedule: {
    date: Date;
    dayOff: boolean;
    time?: { to: number; from: number };
  }[];
}

export interface EventFormValues
  extends Omit<
    Event,
    | 'id'
    | 'theme'
    | 'status'
    | 'product'
    | 'payments'
    | 'locations'
    | 'teamMembers'
    | 'selectedLocations'
  > {
  photo?: UploadFile;
  themeId: number | null;
  floorPlan?: UploadFile;
  productId: number | null;
  teamMembers: User['id'][];
  locations: Partial<EventLocation>[];
  payments: {
    date?: Date;
  }[] &
    Omit<Partial<EventPayment>, 'date'>;
}

export interface DayResultCountProduct extends IdObject {
  units: 10;
  price: number;
  product: Product;
  createdAt: string;
  updatedAt: string;
  productId: number;
  storageId: number;
  demos?: DayResultCount;
  damaged?: DayResultCount;
  beginning?: DayResultCount;
  addedInventory?: DayResultCount;
}

export interface EventDayResult extends IdObject {
  user: User;
  event: Event;
  notes: string;
  userId: number;
  amount: number;
  eventId: number;
  createdAt: string;
  updatedAt: string;
  countProducts: DayResultCountProduct[];
}

export interface EventLocation extends IdObject {
  width?: number;
  number?: string;
  length?: number;
  income?: number;
  eventType: EventType;
  locationType: EventLocationType;
}

export interface DayResultCount {
  cases: number;
  count: number;
  units: number;
  totalPrice: number;
}

export interface EventTeamMember extends User {
  employee?: Employee;
}

export type EventStatus = 'new' | 'offered' | 'accepted';

export const eventPaymentTypeOptions = ['payment', 'paymentPlan'] as const;
export type EventPaymentType = (typeof eventPaymentTypeOptions)[number];
export const eventPaymentTypeLabel: Record<EventPaymentType, string> = {
  payment: 'Payment ',
  paymentPlan: 'Payment Plan',
};

export const eventPaymentTransactionOptions = [
  'deposit',
  'partialPayment',
  'fullPayment',
  'electricity',
  'coi',
  'misc',
];
export type EventPaymentTransaction =
  (typeof eventPaymentTransactionOptions)[number];
export const eventPaymentTransactionLabel: Record<
  EventPaymentTransaction,
  string
> = {
  coi: 'COI',
  misc: 'Misc',
  deposit: 'Deposit',
  electricity: 'Electricity',
  fullPayment: 'Full Payment',
  partialPayment: 'Partial Payment',
};

export enum AdditionalCashManagerType {
  Sale = 'Sale',
  Void = 'Void',
  Beginning = 'Beginning',
  DayResult = 'Day Result',
}

export interface RefundedByCloverCard {
  last4: string;
  cardType: string;
  paymentId: string;
  totalPaid: number;
  processingFee: number;
  totalRefunded: number;
  totalRefundedFee: number;
  availableForRefund: number;
  availableFeeRefund: number;
  availableTipRefund: number;
  availableForRefundWithFee: number;
}

export interface AvailableForRefund {
  cash: number;
  credit: number;
}

export interface EventCheckout extends Omit<FormValues, 'customer'>, IdObject {
  user: User;
  tax?: number;
  event: Event;
  userId: number;
  pdfUrl?: string;
  eventId: number;
  comment?: string;
  subTotal: number;
  taxAmount: number;
  refund?: Refund[];
  items: CartItem[];
  createdAt: string;
  updatedAt: string;
  saleTotal: number;
  grossTotal: number;
  isVoided?: boolean;
  type: CheckoutType;
  cashAmount: number;
  customerId?: number;
  customer?: Customer;
  voidPdfUrl?: string;
  giftPdfUrl?: string;
  creditAmount: number;
  processingFees: number;
  shippingPrice?: string;
  eventOrder?: EventOrder;
  chargeback?: Chargeback;
  isFreeShipping?: boolean;
  exchangeStorageId?: number;
  shippingItems?: CartItem[];
  exchanges?: EventCheckout[];
  shippingItemsTotal?: number;
  exchangeCheckoutId?: number;
  isAllItemsProcessed: boolean;
  syncedCustomers?: Customer[];
  exchangeItemsAmount?: number;
  processingFeesAmount?: number;
  cloverPayments: CloverPayment[];
  refundedByCloverPayments: number;
  isProcessingFeeEnabled?: boolean;
  lostCommissions?: LostCommission[];
  isManualCreditPaymentEnabled?: boolean;
  availableForRefund: AvailableForRefund;
  refundedByCloverCards: RefundedByCloverCard[];
}

export interface EventReport {
  date: string;
  expenses: Payout[];
  checkoutDate: string;
  total: EventReportTotal;
  users: EventReportUser[];
  products: EventReportProduct[];
  employees: EventReportEmployee[];
  commissions: EventReportCommission[];
  paymentMethods: Record<string, number>;
}

export interface EventReportTotalRevenue {
  tax: number;
  tip: number;
  netSales: number;
  grossSales: number;
}

export interface EventReportResponse {
  report: EventReport[];
  total: EventReportTotalRevenue;
}

export interface DailyCountProduct
  extends Omit<
    Inventory,
    'demos' | 'damaged' | 'beginning' | 'addedInventory'
  > {
  demos?: DailyCountProductValue;
  damaged?: DailyCountProductValue;
  beginning?: DailyCountProductValue;
  addedInventory?: DailyCountProductValue;
}

export interface EventPayment extends IdObject {
  date?: string;
  paidBy?: User;
  notes?: string;
  amount?: number;
  cashed?: boolean;
  type: EventPaymentType;
  paymentMethod?: PaymentMethod;
  transaction?: EventPaymentTransaction;
}

export interface EventReportProduct extends Inventory {
  name: string;
  price: number;
  productId: number;
  totalSold: number;
  remaining: number;
  totalRevenue: number;
  productRevenue: number;
}

export interface EventReportTotal {
  tax: number;
  tip: number;
  total: number;
  netSales: number;
  grossSales: number;
  totalAmount: number;
  processingFees: number;
  shippingPrices: number;
}

export interface EventReportUser {
  name: string;
  userId: number;
  bonuses: number;
  advances: number;
  reimbursements: number;
  createdCheckout: number;
}

export interface EventReportEmployee {
  name: string;
  tip?: number;
  email: string;
  total: number;
  netSales: number;
  grossSales: number;
  netSaleContribution: number;
  totalSaleContribution: number;
  grossSaleContribution: number;
}

export interface EventOpenCloseDayRequest extends IdObject {
  notes?: string;
  amount?: number;
  countProducts?: DailyCountProduct[];
}

export interface EventCheckoutRequest extends Partial<FormValues>, IdObject {
  cartId: number;
  subTotal: number;
  shippingPrice?: number;
  exchangeStorageId?: number;
  exchangeCheckoutId?: number;
}

export interface EventEmployeesResponse extends User {
  bonuses: Payout[];
  advances: Payout[];
  employeeId: number;
}

export interface EventReportCommission {
  name: string;
  tips: number;
  email: string;
  totalCommission: number;
}

export interface EventReportRequest {
  id: string;
  isDailyReport?: boolean;
}

export interface DailyCountProductValue {
  cases?: number;
  units?: number;
}

export type LostCommission = Commission;

export const CashManagerType = {
  ...PayoutType,
  ...AdditionalCashManagerType,
};

export interface CategoryDetails extends IdObject {
  date: string;
  notes: string;
  receiptUrl: string;
  employeeId?: number;
  categoryName: string;
  employeeName: string;
  amount: number | string;
  paymentMethod: Nullable<string>;
  source: 'expense' | 'reimbursement';
  createdBy?: Pick<User, 'name' | 'email'>;
}

export interface EventProfitAndLoss {
  profit: number;
  totalTip: number;
  totalTax: number;
  workingDays: number;
  productCost: number;
  totalBonuses: number;
  totalExpenses: number;
  totalNetSales: number;
  payoutItems: Payout[];
  totalGrossSales: number;
  profitPercentage: number;
  totalCommissions: number;
  totalShipmentCost: number;
  totalLocationsRent: number;
  totalLTLStockOrders: number;
  totalReimbursements: number;
  totalProcessingFees: number;
  totalProductSoldCost: number;
  totalAccountingExpenses: number;
  productRevenuePercentage: number;
  categoryDetails: CategoryDetails[];
  totalExpensesWithCommissions: number;
  expensesByCategory: EventProfitAndLossExpensesCategory[];
  accountingExpensesByCategory: EventProfitAndLossExpensesCategory[];
}

export interface EventQuestionnaire extends IdObject {
  user: User;
  event: Event;
  userId: number;
  eventId: number;
  visitors: string;
  weakDays: string;
  boothSize: string;
  comments?: string;
  updatedAt: string;
  createdAt: string;
  repsNextYear: string;
  generalScore: number;
  locationScore: number;
  teamWorkIssue: string;
  logisticsIssue: string;
  houseOwnerInfo: string;
  locationRequest: string;
  additionalBooth: string;
  accommodationScore: number;
  manageEventNextYear: string;
}

export interface EventDashboardStatistics {
  refund: number;
  netSales: number;
  salesTax: number;
  shipping: number;
  totalTips: number;
  grossSales: number;
  totalSales: number;
  processingFee: number;
  totalProducts: number;
  checkoutCounts: number;
  productRevenuePercentage: number;
  dailySales: EventDashboardDailySale[];
  damagedProducts: EventDashboardDamagedProducts;
  topSellingProducts: EventDashboardTopSellingProduct[];
}

export interface EventEmployeeCommission {
  pay: number;
  totalNet: number;
  totalTips: number;
  totalBonus: number;
  totalGross: number;
  totalAdvance: number;
  totalCommissions: number;
  commissions: EventEmployeeCommissionInfo[];
}

export interface EventInventoryStatisticEnd
  extends Pick<
    EventInventoryStatisticStart,
    'name' | 'price' | 'masters' | 'singles'
  > {
  demos: number;
  total: number;
  damaged: number;
  unitPerCase: string;
}

export interface EventInventoryStatisticSold
  extends Pick<EventInventoryStatisticStart, 'name' | 'price'> {
  quantity: number;
  discount: number;
  productId: number;
  totalPrice: number;
  productRevenue: number;
}

export interface EventEmployeeCommissionInfo {
  tip: number;
  pay: number;
  date: string;
  netSale: number;
  bonuses: number;
  advances: number;
  grossSale: number;
  commissions: number;
}

export interface CashManagerEntry {
  date: string;
  amount: number;
  balance: number;
  employeeName: string;
  type: (typeof CashManagerType)[keyof typeof CashManagerType];
}

export interface CashManagerExpenseAndPayout extends IdObject {
  date: string;
  type: string;
  notes: string;
  amount: number;
  category: string;
  employee: string;
  sortDate: string;
  receiptUrl: string;
}

export interface EventCashManagerResponse {
  balance: number;
  totalOut: number;
  refundAmount: number;
  totalCollected: number;
  entries: CashManagerEntry[];
  payoutsAndExpenses: CashManagerExpenseAndPayout[];
}

export interface EventInventoryStatistic {
  end: EventInventoryStatisticEnd[];
  sold: EventInventoryStatisticSold[];
  start: EventInventoryStatisticStart[];
}

export interface EventInventoryStatisticStart {
  name: string;
  total: number;
  price: string;
  masters: number;
  singles: number;
}

export interface EventTopSeller {
  user: User;
  userId: number;
  netSales: number;
  commission: number;
  totalSales: number;
}

export type EventQuestionnaireRequest = Omit<
  EventQuestionnaire,
  'user' | 'event' | 'createdAt' | 'updatedAt'
>;

export type UpdateEventQuestionnaireRequest = IdObject &
  Partial<EventQuestionnaireRequest>;

export interface EventStockOrdersInfoResponse {
  stockOrders: StockOrder[];
  stockTransfers: StockTransfer[];
}

export interface EmployeeCheckout {
  monthSales: number;
  yearlySales: number;
  checkouts: EventCheckout[];
}

export interface EventDashboardTopSellingProduct {
  sales: number;
  product: Product;
  quantity: number;
}

export interface EventTopSellerResponse {
  checkoutsTotal: number;
  topSellers: EventTopSeller[];
}

export interface EventDashboardDamagedProducts {
  totalCost: number;
  totalCount: number;
}

export interface EventProfitAndLossExpensesCategory {
  name: string;
  amount: number;
}

export interface EventEmployeeCommissionsRequest extends IdObject {
  userId: number;
}

export type EventListRequest = SearchRequest &
  Partial<Event> &
  PaginationRequest;

export interface EventDashboardStatisticsRequest {
  id: string;
  endDate: string;
  startDate: string;
}

export interface EmployeeCheckoutRequest extends IdObject {
  eventId?: string;
  type?: CheckoutType;
}

export interface ReceiptDetailsRequest extends IdObject {
  receiptId: string;
}

export interface EventDashboardDailySale {
  date: string;
  sales: number;
}

export interface EventTopSellerRequest {
  ids?: string;
  date?: string;
}

export interface ReceiptHTMLRequest {
  id: string;
  receiptId: string;
  isGiftReceipt?: boolean;
}

export interface ReceiptsRequest
  extends IdObject,
    SearchRequest,
    PaginationRequest {
  usersId?: string;
}

export type ReceiptResponse = PaginationData<'rows', EventCheckout>;

export type EventListResponseData = BaseResponse<EventListResponse>;

export type EventListResponse = PaginationData<'rows', Event>;

export interface ReportPdfResponse {
  jobId: string;
}

export interface TotalSalesForEvent {
  netSales: number;
  grossSales: number;
}

export interface TotalSalesForEventRequest {
  eventIds: string;
}

export interface TopEventsRequest {
  endDate?: string;
  startDate: string;
}

export interface TopEvent {
  eventId: number;
  netSales: number;
  eventName: string;
  grossSales: number;
  checkoutsCount: number;
}

export interface TopEventsResponse {
  totalNet: number;
  totalGross: number;
  topEvents: TopEvent[];
}
